import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import type {
    ChecklistFileItem,
    ChecklistRelatedItem,
    ChecklistToc,
    CollectionDataResponse,
} from "../types";
import {ResourceType} from "../types";
import {FileRefRow} from "./components/FileRefRow";
import {DocumentGeneratorRefRow} from "./components/DocumentGeneratorRefRow";
import styles from "./checklist-documents-list.module.css";

type DocumentsListProps = {
    collectionData?: CollectionDataResponse;
    viewFilteredToc: ChecklistToc;
    effectiveRootNodeId: string;
    onToggleDocumentSelected: (topicId: string, selected: boolean) => void;
    activeDocumentId?: string;
};

type RelatedItems = Set<ChecklistRelatedItem>;

export const getAllRelatedDocuments = (
    viewFilteredToc: ChecklistToc,
    rootNodeId: string,
): {
    orderedTopLevelCheckItems: {id: string; name: string}[];
    checkItemDocuments: Record<string, RelatedItems>;
    checkItemFiles: Record<
        string,
        Set<{fileItem: ChecklistFileItem; relatedItem: ChecklistRelatedItem | null}>
    >;
} => {
    const rootNode = viewFilteredToc[rootNodeId];
    if (!rootNode) {
        return {
            orderedTopLevelCheckItems: [],
            checkItemDocuments: {},
            checkItemFiles: {},
        };
    }

    const orderedTopLevelCheckItems: {id: string; name: string}[] = [];
    (rootNode.children || []).forEach((childId) => {
        const child = viewFilteredToc[childId];
        if (child) {
            orderedTopLevelCheckItems.push({id: childId, name: child.title || ""});
        }
    });

    const checkItemDocuments: Record<string, RelatedItems> = {};
    const checkItemFiles: Record<
        string,
        Set<{fileItem: ChecklistFileItem; relatedItem: ChecklistRelatedItem | null}>
    > = {};

    const seenRelatedIds = new Set<string>();
    const seenFileIds = new Set<string>();

    (rootNode.children || []).forEach((checkItemId) => {
        const relatedItems = new Set<ChecklistRelatedItem>();
        const relatedFiles = new Set<{
            fileItem: ChecklistFileItem;
            relatedItem: ChecklistRelatedItem | null;
        }>();

        const traverse = (nodeId: string) => {
            const node = viewFilteredToc[nodeId];
            if (!node) return;

            if (node.related) {
                for (const related of node.related) {
                    const isFile = node.files?.some(
                        (f) => f.topicId === related.topicId,
                    );
                    const isDocGen =
                        related.resourceTypeId === ResourceType.documentGenerator;
                    const isPDF =
                        String(related.resourceTypeId) === ResourceType.pdf;

                    if (
                        (isPDF || (!isFile && isDocGen)) &&
                        !seenRelatedIds.has(related.topicId)
                    ) {
                        seenRelatedIds.add(related.topicId);
                        relatedItems.add(related);
                    }
                }
            }

            if (node.files) {
                for (const file of node.files) {
                    if (
                        !seenFileIds.has(file.topicId) &&
                        !seenRelatedIds.has(file.topicId)
                    ) {
                        seenFileIds.add(file.topicId);
                        relatedFiles.add({
                            fileItem: file,
                            relatedItem:
                                node.related?.find(
                                    (r) => r.topicId === file.topicId,
                                ) ?? null,
                        });
                    }
                }
            }

            node.children?.forEach(traverse);
        };

        traverse(checkItemId);
        checkItemDocuments[checkItemId] = relatedItems;
        checkItemFiles[checkItemId] = relatedFiles;
    });

    return {orderedTopLevelCheckItems, checkItemDocuments, checkItemFiles};
};

export const DocumentsList = ({
    collectionData,
    viewFilteredToc,
    effectiveRootNodeId,
    onToggleDocumentSelected,
    activeDocumentId,
}: DocumentsListProps) => {
    const {t} = useTranslation();
    const location = useLocation();

    const checklistBasePath = useMemo(() => {
        const parts = location.pathname.split("/");
        // /type/name/id → first 4 segments
        return parts.slice(0, 4).join("/");
    }, [location.pathname]);

    const documents = useMemo(() => {
        if (!effectiveRootNodeId || !viewFilteredToc[effectiveRootNodeId])
            return null;
        return getAllRelatedDocuments(viewFilteredToc, effectiveRootNodeId);
    }, [viewFilteredToc, effectiveRootNodeId]);

    if (!documents) return null;

    const {orderedTopLevelCheckItems, checkItemDocuments, checkItemFiles} =
        documents;

    const checkItemsWithDocuments = orderedTopLevelCheckItems.filter(
        (item) =>
            (checkItemDocuments[item.id]?.size ?? 0) > 0 ||
            (checkItemFiles[item.id]?.size ?? 0) > 0,
    );

    if (checkItemsWithDocuments.length === 0) {
        return (
            <span className={styles.noDocuments}>
                {t("Info.NoAvailableDocuments")}
            </span>
        );
    }

    return (
        <div className={styles.checklistDocumentsList}>
            {checkItemsWithDocuments.map((checkItem) => {
                const files = Array.from(checkItemFiles[checkItem.id] ?? []);
                const docs = Array.from(checkItemDocuments[checkItem.id] ?? []);

                return (
                    <div
                        key={checkItem.id}
                        className={styles.checklistDocumentsGroup}
                    >
                        <h3
                            className={
                                styles.checklistDocumentsGroupHeading
                            }
                        >
                            {checkItem.name}
                        </h3>
                        <ul className={styles.relatedList}>
                            {files.map((fd) => (
                                <li
                                    key={fd.fileItem.topicId}
                                    className={styles.relatedFile}
                                >
                                    <FileRefRow
                                        fileItem={fd.fileItem}
                                        relatedItem={
                                            fd.relatedItem ?? undefined
                                        }
                                    />
                                </li>
                            ))}
                            {docs.map((doc) => (
                                <li
                                    key={doc.topicId}
                                    className={`${styles.relatedFile}${
                                        activeDocumentId === doc.topicId
                                            ? ` ${styles.activeDocument}`
                                            : ""
                                    }`}
                                >
                                    <DocumentGeneratorRefRow
                                        relatedItem={doc}
                                        collectionData={collectionData}
                                        onToggle={onToggleDocumentSelected}
                                        checklistBasePath={checklistBasePath}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
};
